import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  getVerifyStatus as defaultGetStatus,
  resendVerifyLink as defaultResend,
} from "../lib/api";

// Click-to-chat WhatsApp verification step.
// Props:
//   token           — token used for polling (invitation token OR signup
//                     verificationToken, depending on caller)
//   waLink          — wa.me URL
//   botPhone        — bot's WhatsApp number (digits only)
//   verifyMessage   — exact text the user will send ("verify abc123def456")
//   phone           — phone (masked) for the help text
//   expiresAt       — ISO string of when the verifyToken expires
//   onVerified(user)— called when the poll sees verified=true
//   getStatus(token)→ optional override; defaults to invitation verify-status
//   doResend(token) → optional override; defaults to invitation resend-link
export default function WhatsAppVerifyStep({
  token,
  waLink: initialLink,
  botPhone: initialBot,
  verifyMessage: initialMsg,
  phone,
  expiresAt: initialExpiresAt,
  onVerified,
  getStatus,
  doResend,
}) {
  const getStatusFn = getStatus || defaultGetStatus;
  const doResendFn = doResend || defaultResend;
  const [waLink, setWaLink] = useState(initialLink);
  const [botPhone, setBotPhone] = useState(initialBot);
  const [verifyMessage, setVerifyMessage] = useState(initialMsg);
  const [, setExpiresAt] = useState(initialExpiresAt);
  const [status, setStatus] = useState("waiting"); // waiting | expired | done | error
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(60);
  const [resending, setResending] = useState(false);
  const [copied, setCopied] = useState(false);
  const stopRef = useRef(false);

  // Poll verify-status every 2 seconds.
  useEffect(() => {
    stopRef.current = false;
    const fetchStatus = getStatus || defaultGetStatus;
    const poll = async () => {
      if (stopRef.current) return;
      try {
        const data = await fetchStatus(token);
        if (data.verified) {
          setStatus("done");
          stopRef.current = true;
          onVerified?.(data.user);
          return;
        }
        if (data.expired) {
          setStatus("expired");
          stopRef.current = true;
          return;
        }
      } catch (err) {
        // Network blip — keep polling
        console.warn("[verify-poll] error:", err.message);
      }
      if (!stopRef.current) setTimeout(poll, 2000);
    };
    const t = setTimeout(poll, 1500);
    return () => {
      stopRef.current = true;
      clearTimeout(t);
    };
  }, [token, onVerified, getStatus]);

  // Cooldown timer for resend button.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setError("");
    try {
      const data = await doResendFn(token);
      setWaLink(data.waLink);
      setBotPhone(data.botPhone);
      setVerifyMessage(data.verifyMessage);
      setExpiresAt(data.expiresAt);
      setStatus("waiting");
      setCooldown(60);
      stopRef.current = false;
      // restart polling
      setTimeout(async () => {
        try {
          const s = await getStatusFn(token);
          if (s.verified) {
            setStatus("done");
            onVerified?.(s.user);
          }
        } catch {}
      }, 1500);
    } catch (err) {
      if (err.status === 429) {
        setError("Please wait a moment before requesting another link.");
      } else {
        setError(err.message || "Could not get a new link.");
      }
    } finally {
      setResending(false);
    }
  };

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(verifyMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  const prettyBot = botPhone
    ? `+${botPhone.slice(0, 2)} ${botPhone.slice(2, 7)} ${botPhone.slice(7)}`
    : "";

  // ── Done state ─────────────────────────────────────────────────────────
  if (status === "done") {
    return (
      <div>
        <h3 style={{ margin: 0, color: "var(--text)" }}>Phone verified ✅</h3>
        <p style={{ color: "var(--text-muted)", marginTop: 8 }}>
          Redirecting you to sign in…
        </p>
      </div>
    );
  }

  // ── Expired state ──────────────────────────────────────────────────────
  if (status === "expired") {
    return (
      <div>
        <p style={{ color: "var(--text)", marginTop: 0 }}>
          Your verification link expired.
        </p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleResend}
          disabled={resending}
          style={{ width: "100%", justifyContent: "center", marginTop: 12 }}
        >
          {resending ? "Generating…" : "Get a new verification link"}
        </button>
      </div>
    );
  }

  // ── Waiting state (the main UI) ────────────────────────────────────────
  return (
    <div>
      <p style={{ color: "var(--text-muted)", marginTop: 0, marginBottom: 12 }}>
        Tap the button below to send a one-time verification message from your
        WhatsApp on{" "}
        <strong style={{ color: "var(--text)" }}>{phone}</strong>.
      </p>

      {error && (
        <div
          style={{
            background: "rgba(239,68,68,.1)",
            border: "1px solid var(--error)",
            color: "var(--error)",
            padding: "10px 12px",
            borderRadius: 8,
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      <a
        href={waLink}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-primary"
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "center",
          marginBottom: 14,
          textDecoration: "none",
        }}
      >
        📱 Verify on WhatsApp
      </a>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          margin: "16px 0 12px",
          color: "var(--text-muted)",
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 1,
        }}
      >
        <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
        <span>or scan with your phone</span>
        <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: 14,
          background: "#fff",
          borderRadius: 10,
          marginBottom: 12,
        }}
      >
        <QRCodeSVG value={waLink} size={170} level="M" includeMargin={false} />
      </div>

      <div
        style={{
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          padding: 12,
          fontSize: 12.5,
          color: "var(--text-muted)",
          marginBottom: 12,
        }}
      >
        Or open WhatsApp manually and send{" "}
        <code
          style={{
            color: "var(--text)",
            background: "rgba(255,255,255,0.05)",
            padding: "1px 6px",
            borderRadius: 4,
          }}
        >
          {verifyMessage}
        </code>{" "}
        to <strong style={{ color: "var(--text)" }}>{prettyBot}</strong>
        <button
          type="button"
          onClick={copyMessage}
          className="btn btn-ghost"
          style={{ marginLeft: 8, padding: "2px 8px", fontSize: 12 }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          color: "var(--text-muted)",
          fontSize: 13,
          marginBottom: 14,
        }}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: "var(--primary, #6366f1)",
            animation: "verifyPulse 1.4s ease-in-out infinite",
          }}
        />
        Waiting for your WhatsApp message…
        <style>{`@keyframes verifyPulse{0%,100%{opacity:.3}50%{opacity:1}}`}</style>
      </div>

      <button
        type="button"
        onClick={handleResend}
        disabled={cooldown > 0 || resending}
        className="btn btn-ghost"
        style={{ width: "100%", justifyContent: "center" }}
      >
        {resending
          ? "Generating…"
          : cooldown > 0
            ? `Get a new link in ${cooldown}s`
            : "Get a new link"}
      </button>
    </div>
  );
}
